import { FirebaseError } from 'firebase/app';
import type { EventSettings } from '../services/events';

export function pickLatestSession<T extends { status: string; createdAt: string }>(
  sessions: T[],
  activeOnly: boolean
): T | null {
  const candidates = activeOnly ? sessions.filter(session => session.status === 'active') : [...sessions];
  candidates.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return candidates[0] ?? null;
}

// Server refusals the co-approval DJ page shows as written (ITER-194)
const PASS_THROUGH_MESSAGES = [
  'Someone else is reviewing this song',
  'Your name is already set',
  'Enter your name first',
  'Enter a name of 1 to 40 characters',
];

export function shareLinkErrorMessage(error: unknown, linkName: string): string {
  if (error instanceof FirebaseError) {
    if (PASS_THROUGH_MESSAGES.includes(error.message)) return error.message;
    switch (error.code) {
      case 'functions/permission-denied':
        return `This ${linkName} link is no longer active`;
      case 'functions/not-found':
        return 'That song is no longer available';
      case 'functions/failed-precondition':
        return 'That song was already handled — refreshing';
    }
  }
  return 'Something went wrong — please try again';
}

// The link was revoked or removed: every later action on the page will be refused too
export function isLinkClosedError(error: unknown): boolean {
  return error instanceof FirebaseError && error.code === 'functions/permission-denied';
}

export function isAlreadyHandledError(error: unknown): boolean {
  return error instanceof FirebaseError && error.code === 'functions/failed-precondition';
}

type LegacyTokenKeys = { coApprovalToken?: unknown; vipTriggerToken?: unknown };

export function omitLegacyTokens<Settings extends Partial<EventSettings> & LegacyTokenKeys>(
  settings: Settings | undefined | null
): Settings {
  const cleaned = { ...(settings ?? {}) } as Settings;
  delete cleaned.coApprovalToken;
  delete cleaned.vipTriggerToken;
  return cleaned;
}
